import React, { useState, useEffect } from "react";
import api from "../services/api";
import { authService } from "../services/authService";
import {
    PermissionsContext,
    Permissions,
} from "../hooks/usePermissions";

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [permissions, setPermissions] = useState<Permissions | null>(null);
    const [isCollaborator, setIsCollaborator] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPermissions();
    }, []);

    async function loadPermissions() {
        try {
            setLoading(true);
            const companyId = authService.getCompanyId();
            const token = authService.getToken();

            // Se não tiver token ou companyId, assumir owner
            if (!token || !companyId) {
                setPermissions({ full_access: true });
                setIsCollaborator(false);
                setLoading(false);
                return;
            }

            const response = await api.get(
                `/api/collaborators/me?companyId=${companyId}`
            );

            if (
                response.data.status === "success" &&
                response.data.data?.permissions
            ) {
                setPermissions(response.data.data.permissions);
                setIsCollaborator(true);
            } else {
                // Resposta sem permissões = não é colaborador
                setPermissions({ full_access: true });
                setIsCollaborator(false);
            }
        } catch (error) {
            const status = (error as { response?: { status?: number } })
                ?.response?.status;

            // 404 ou 403 = não é colaborador (owner)
            if (status === 404 || status === 403) {
                setPermissions({ full_access: true });
                setIsCollaborator(false);
            } else {
                // Outro erro = problema real, não assumir nada
                // Por segurança, dar permissões vazias (sem acesso)
                setPermissions({});
                setIsCollaborator(false);
            }
        } finally {
            setLoading(false);
        }
    }

    async function refreshPermissions() {
        await loadPermissions();
    }

    function hasPermission(permission: string): boolean {
        if (!permissions) return false;
        if (permissions.full_access === true) return true;

        const parts = permission.split(".");

        // Permissão simples (ex: "full_access")
        if (parts.length === 1) {
            return permissions[parts[0]] === true;
        }

        // Permissão aninhada (ex: "sales.view")
        if (parts.length === 2) {
            const [module, action] = parts;
            const moduleValue = permissions[module];
            if (moduleValue && typeof moduleValue === "object") {
                return moduleValue[action] === true;
            }
            return false;
        }

        return false;
    }

    function getFirstAvailableRoute(): string {
        // Mapeamento de permissões para rotas na ordem de prioridade
        const routePermissionMap = [
            { route: "/dashboard", permission: "dashboard.view" },
            { route: "/sales", permission: "sales.view" },
            { route: "/abandoned", permission: "sales.abandoned" },
            { route: "/subscriptions", permission: "subscriptions.view" },
            { route: "/products", permission: "products.view" },
            { route: "/finance", permission: "financial.view" },
            { route: "/apps", permission: "integrations.full" },
            { route: "/collaborators", permission: "collaborators.manage" },
        ];

        // Se tem full_access, vai para dashboard
        if (permissions?.full_access === true) {
            return "/dashboard";
        }

        // Procura a primeira rota que o usuário tem permissão
        for (const { route, permission } of routePermissionMap) {
            if (hasPermission(permission)) {
                return route;
            }
        }

        // Se não tiver nenhuma permissão, redireciona para página de sem acesso
        // (isso não deveria acontecer em condições normais)
        return "/no-permission";
    }

    return (
        <PermissionsContext.Provider
            value={{
                permissions,
                isCollaborator,
                hasPermission,
                getFirstAvailableRoute,
                loading,
                refreshPermissions,
            }}
        >
            {children}
        </PermissionsContext.Provider>
    );
};
